import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FactCheck as FactCheckIcon,
  Print as PrintIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { openVoucherPrint } from '../../services/expenseOperationsApi';
import {
  formatAmount,
  formatDate,
  formatDateTime,
  formatTotals,
} from '../../services/expenseVouchersApi';
import { approvalsApi, STAGE_META, MAX_COMMENT } from '../../services/expenseApprovalsApi';
import {
  useApprovalsBase,
  VoucherStatusChip,
  vendorLabel,
  historyLabel,
  paymentTypeLabel,
  GROUP_LABELS,
  ROUTE_LABELS,
} from '../../components/expense/voucherShared';
import BillList from '../../components/expense/BillList';
import ApprovalProgress from '../../components/expense/ApprovalProgress';
import CommentDialog from '../../components/expense/CommentDialog';

const headerCell = { color: 'white', fontWeight: 600, whiteSpace: 'nowrap' };

const Info = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" component="div">{children || '-'}</Typography>
  </Grid>
);

const ROUTE_HELP = {
  REPORTING: 'At least one Reporting Approver must approve.',
  MANAGEMENT: 'At least one Management Approver must approve.',
  BOTH: 'One Reporting and one Management approval are needed, in any order.',
  FINALIZE: 'Approve the voucher now, without Reporting or Management approval.',
};

const names = (people = []) => people.map((p) => p.name || p.email).join(', ');


// Shared approval review screen. Actions shown are exactly those the server reports for
// the signed-in user (Accounts: route / finalize / reject / send back; approvers: approve /
// reject). The server re-checks every action.
const ExpenseApprovalReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const base = useApprovalsBase();
  const [voucher, setVoucher] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [routeOption, setRouteOption] = useState('');
  const [note, setNote] = useState('');
  const [dialog, setDialog] = useState(null); // 'reject' | 'sendBack'

  const load = useCallback(async () => {
    try {
      const res = await approvalsApi.get(id);
      setVoucher(res.data);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => {
    setVoucher(null);
    setRouteOption('');
    setNote('');
    load();
  }, [load]);

  const run = async (call) => {
    setBusy(true);
    try {
      const res = await call();
      toast.success(res.message || 'Done');
      setVoucher(res.data);
      setNote('');
      setRouteOption('');
      setDialog(null);
    } catch (e) {
      toast.error(e.message);
      // Someone else acted first, or the state changed: show the current voucher
      if (e.status === 409 || e.status === 403) {
        setDialog(null);
        load();
      }
    } finally {
      setBusy(false);
    }
  };

  const noteValue = note.trim() || undefined;
  const noteTooLong = note.trim().length > MAX_COMMENT;

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={error.status === 404 || error.status === 403 ? 'warning' : 'error'}>
          {error.status === 404 ? 'Voucher not found.' : error.message}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(base)} sx={{ mt: 2, textTransform: 'none' }}>Back to Approvals</Button>
      </Box>
    );
  }

  if (!voucher) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  const { actions = {}, approval = {} } = voucher;
  const stage = STAGE_META[approval.stage] || { label: approval.stage, color: 'default' };
  const blockedGroups = (approval.groups || []).filter((g) => g.blocked).map((g) => g.group);
  const options = voucher.routingOptions?.options || [];
  const hasAnyAction = actions.canRoute || actions.canApprove || actions.canReject || actions.canSendBack;

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FactCheckIcon color="primary" />
            Review {voucher.voucherNumber}
            <VoucherStatusChip status={voucher.status} size="medium" />
            <Chip label={stage.label} color={stage.color} variant="outlined" />
          </Typography>
        </Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(base)} sx={{ textTransform: 'none' }}>Approvals</Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => openVoucherPrint(voucher._id)} sx={{ textTransform: 'none' }}>Print / PDF</Button>
      </Box>

      {actions.isOwnVoucher && (
        <Alert severity="info" sx={{ mb: 2 }}>This is your own voucher. You cannot approve, reject or route it.</Alert>
      )}
      {blockedGroups.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {blockedGroups.map((g) => GROUP_LABELS[g]).join(' and ')} approval is blocked: every assigned approver is inactive.
          {actions.canRoute ? ' Re-route, finalize, reject or send the voucher back below.' : ' Accounts must resolve it.'}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Voucher information</Typography>
        <Grid container spacing={2}>
          <Info label="Employee">{voucher.employeeSnapshot?.name}</Info>
          <Info label="Department">{voucher.employeeSnapshot?.department}</Info>
          <Info label="Voucher Number">{voucher.voucherNumber}</Info>
          <Info label="Voucher Date">{formatDate(voucher.voucherDate)}</Info>
          <Info label="Invoice Number">{voucher.invoiceNumber}</Info>
          <Info label="Submitted">{formatDateTime(voucher.submittedAt)}</Info>
          <Info label="Total">{formatTotals(voucher)}</Info>
          <Info label="Current Stage">{stage.label}</Info>
        </Grid>
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>Expense lines</Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 2, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {['Date', 'Vendor', 'Category', 'Head', 'Payment Type', 'Description', 'Currency', 'Amount', 'Bills'].map((h) => (
                <TableCell key={h} sx={headerCell}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(voucher.lines || []).map((l) => (
              <TableRow key={l._id}>
                <TableCell>{formatDate(l.expenseDate)}</TableCell>
                <TableCell>{vendorLabel(l.vendor)}</TableCell>
                <TableCell>{l.categoryName || '-'}</TableCell>
                <TableCell>{l.headName || '-'}</TableCell>
                <TableCell>{paymentTypeLabel(l)}</TableCell>
                <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>{l.description || '-'}</TableCell>
                <TableCell>{l.currency || '-'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{formatAmount(l.amount)}</TableCell>
                <TableCell>
                  <BillList voucherId={voucher._id} bills={l.bills} onDownload={(bill) => approvalsApi.downloadBill(voucher._id, bill)} />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={7} align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
              <TableCell colSpan={2} sx={{ fontWeight: 600 }}>{formatTotals(voucher)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {(approval.requiredGroups || []).length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Assigned approvers</Typography>
          <ApprovalProgress voucher={voucher} blockedGroups={blockedGroups} />
        </Paper>
      )}

      {hasAnyAction && (
        <Paper sx={{ p: 2, mb: 3 }} data-testid="approval-actions">
          <Typography variant="h6" sx={{ mb: 1 }}>Your action</Typography>

          {actions.canRoute && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {voucher.status === 'PENDING_APPROVAL' ? 'Re-route (replaces the current approval chain)' : 'Route to'}
              </Typography>
              <RadioGroup value={routeOption} onChange={(e) => setRouteOption(e.target.value)}>
                {options.map(({ option, available }) => {
                  const approvers = option === 'REPORTING' ? voucher.routingOptions.reportingApprovers
                    : option === 'MANAGEMENT' ? voucher.routingOptions.managementApprovers : null;
                  const detail = !available
                    ? 'No active approver assigned for this employee'
                    : approvers ? `${ROUTE_HELP[option]} Approvers: ${names(approvers)}` : ROUTE_HELP[option];
                  return (
                    <FormControlLabel
                      key={option}
                      value={option}
                      disabled={!available || busy}
                      control={<Radio size="small" />}
                      label={(
                        <Box>
                          <Typography variant="body2">{ROUTE_LABELS[option]}</Typography>
                          <Typography variant="caption" color={available ? 'text.secondary' : 'error'}>{detail}</Typography>
                        </Box>
                      )}
                      sx={{ alignItems: 'flex-start', mb: 0.5, '& .MuiRadio-root': { pt: 0.25 } }}
                    />
                  );
                })}
              </RadioGroup>
            </Box>
          )}

          {(actions.canRoute || actions.canApprove) && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Approval note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              error={noteTooLong}
              helperText={`${note.trim().length}/${MAX_COMMENT}`}
              sx={{ mb: 2 }}
            />
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {actions.canRoute && (
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                disabled={busy || !routeOption || noteTooLong}
                onClick={() => run(() => approvalsApi.route(voucher._id, { version: voucher.version, option: routeOption, note: noteValue }))}
                sx={{ textTransform: 'none' }}
              >
                {routeOption === 'FINALIZE' ? 'Finalize & approve' : 'Send for approval'}
              </Button>
            )}
            {actions.canApprove && actions.approveGroups.map((group) => (
              <Button
                key={group}
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                disabled={busy || noteTooLong}
                onClick={() => run(() => approvalsApi.approve(voucher._id, { version: voucher.version, group, note: noteValue }))}
                sx={{ textTransform: 'none' }}
              >
                {actions.approveGroups.length > 1 ? `Approve as ${GROUP_LABELS[group]}` : 'Approve'}
              </Button>
            ))}
            {actions.canReject && (
              <Button variant="outlined" color="error" startIcon={<CancelIcon />} disabled={busy} onClick={() => setDialog('reject')} sx={{ textTransform: 'none' }}>
                Reject
              </Button>
            )}
            {actions.canSendBack && (
              <Button variant="outlined" color="warning" startIcon={<UndoIcon />} disabled={busy} onClick={() => setDialog('sendBack')} sx={{ textTransform: 'none' }}>
                Send Back
              </Button>
            )}
          </Box>
          {actions.approveGroups?.length > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              You are assigned in both approval groups: each group needs its own approval.
            </Typography>
          )}
        </Paper>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>Approval history</Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {['Date / Time', 'By', 'Action', 'Status', 'Comment'].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...(voucher.history || [])].reverse()
              .filter((h) => !['UPDATED', 'BILL_UPLOADED', 'BILL_DELETED'].includes(h.action))
              .map((h, i) => (
                <TableRow key={`${h.at}-${i}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(h.at)}</TableCell>
                  <TableCell>{h.byName || '-'}</TableCell>
                  <TableCell>{historyLabel(h)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {h.fromStatus && h.toStatus && h.fromStatus !== h.toStatus ? `${h.fromStatus} → ${h.toStatus}` : (h.toStatus || '')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{h.comment || ''}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CommentDialog
        open={dialog === 'reject'}
        title={`Reject ${voucher.voucherNumber}`}
        message="Rejection is final. The employee cannot edit or resubmit this voucher and must create a new one."
        label="Rejection reason"
        confirmLabel="Reject voucher"
        color="error"
        busy={busy}
        onClose={() => setDialog(null)}
        onConfirm={(reason) => run(() => approvalsApi.reject(voucher._id, { version: voucher.version, reason }))}
      />
      <CommentDialog
        open={dialog === 'sendBack'}
        title={`Send ${voucher.voucherNumber} back to the employee`}
        message="The voucher returns to the employee as an editable draft. Any approvals so far are cleared."
        label="Send Back comment"
        confirmLabel="Send back"
        color="warning"
        busy={busy}
        onClose={() => setDialog(null)}
        onConfirm={(comment) => run(() => approvalsApi.sendBack(voucher._id, { version: voucher.version, comment }))}
      />
    </Box>
  );
};

export default ExpenseApprovalReview;
